import { z } from 'zod'
import { v4 as uuidv4 } from 'uuid'
import { makeTypeGuard } from '@customTypes/cloudmix/typeUtils'

export const componentIdSchema = z.string().default(() => uuidv4())

export const COMPONENT_TYPES = {
  mixer: 'mixer',
  testSource: 'testSource',
  webSource: 'webSource',
  rtmpDestination: 'rtmpDestination',
  srtDestination: 'srtDestination',
  liveInputSource: 'liveInputSource',
  zixiSource: 'zixiSource',
  imageSource: 'imageSource',
  contributor: 'contributor',
  display: 'display',
  playerSource: 'playerSource',
  liveOutputDestination: 'liveOutputDestination',
  zixiDestination: 'zixiDestination',
} as const

export const componentTypeSchema = z.union([
  z.literal(COMPONENT_TYPES.mixer),
  z.literal(COMPONENT_TYPES.testSource),
  z.literal(COMPONENT_TYPES.webSource),
  z.literal(COMPONENT_TYPES.rtmpDestination),
  z.literal(COMPONENT_TYPES.srtDestination),
  z.literal(COMPONENT_TYPES.liveInputSource),
  z.literal(COMPONENT_TYPES.zixiSource),
  z.literal(COMPONENT_TYPES.imageSource),
  z.literal(COMPONENT_TYPES.contributor),
  z.literal(COMPONENT_TYPES.display),
  z.literal(COMPONENT_TYPES.playerSource),
  z.literal(COMPONENT_TYPES.liveOutputDestination),
  z.literal(COMPONENT_TYPES.zixiDestination),
])

export const SOURCE_COMPONENT_TYPES = [
  COMPONENT_TYPES.testSource,
  COMPONENT_TYPES.webSource,
  COMPONENT_TYPES.liveInputSource,
  COMPONENT_TYPES.imageSource,
  COMPONENT_TYPES.playerSource,
  COMPONENT_TYPES.zixiSource,
] as const

export const DESTINATION_COMPONENT_TYPES = [
  COMPONENT_TYPES.rtmpDestination,
  COMPONENT_TYPES.srtDestination,
  COMPONENT_TYPES.display,
  COMPONENT_TYPES.liveOutputDestination,
  COMPONENT_TYPES.zixiDestination,
] as const

export const SOURCE_AND_MIXER_COMPONENT_TYPES = [
  ...SOURCE_COMPONENT_TYPES,
  COMPONENT_TYPES.mixer,
] as const

export const SOURCE_CONTRIBUTOR_AND_MIXER_COMPONENT_TYPES = [
  ...SOURCE_COMPONENT_TYPES,
  COMPONENT_TYPES.mixer,
  COMPONENT_TYPES.contributor,
] as const

export const RECORDABLE_COMPONENT_TYPES = [
  COMPONENT_TYPES.testSource, // TODO(ajb): Remove after testing
  COMPONENT_TYPES.liveInputSource,
  COMPONENT_TYPES.mixer,
  COMPONENT_TYPES.contributor,
  COMPONENT_TYPES.zixiSource,
] as const

export const isSourceComponentType = makeTypeGuard(SOURCE_COMPONENT_TYPES)
export const isDestinationComponentType = makeTypeGuard(DESTINATION_COMPONENT_TYPES)
export const isSourceAndMixerComponentType = makeTypeGuard(SOURCE_AND_MIXER_COMPONENT_TYPES)
export const isSourceContributorAndMixerComponentType = makeTypeGuard(SOURCE_CONTRIBUTOR_AND_MIXER_COMPONENT_TYPES)
export const isRecordableComponentType = makeTypeGuard(RECORDABLE_COMPONENT_TYPES)

export const sourceComponentTypeSchema = z.union([
  z.literal(COMPONENT_TYPES.testSource),
  z.literal(COMPONENT_TYPES.webSource),
  z.literal(COMPONENT_TYPES.liveInputSource),
  z.literal(COMPONENT_TYPES.imageSource),
  z.literal(COMPONENT_TYPES.playerSource),
  z.literal(COMPONENT_TYPES.zixiSource),
])

export const destinationComponentTypeSchema = z.union([
  z.literal(COMPONENT_TYPES.rtmpDestination),
  z.literal(COMPONENT_TYPES.srtDestination),
  z.literal(COMPONENT_TYPES.display),
  z.literal(COMPONENT_TYPES.liveOutputDestination),
  z.literal(COMPONENT_TYPES.zixiDestination),
])

export const sourceAndMixerComponentTypeSchema = z.union([
  sourceComponentTypeSchema,
  z.literal(COMPONENT_TYPES.mixer),
])

export const sourceContributorAndMixerComponentTypeSchema = z.union([
  sourceComponentTypeSchema,
  z.literal(COMPONENT_TYPES.mixer),
  z.literal(COMPONENT_TYPES.contributor),
])

export const sourceContributorAndMixerType: readonly string[] =
  SOURCE_CONTRIBUTOR_AND_MIXER_COMPONENT_TYPES

export const recordableComponentsTypeSchema = z.union([
  z.literal(COMPONENT_TYPES.testSource), // TODO(ajb): Remove after testing
  z.literal(COMPONENT_TYPES.liveInputSource),
  z.literal(COMPONENT_TYPES.mixer),
  z.literal(COMPONENT_TYPES.contributor),
  z.literal(COMPONENT_TYPES.zixiSource),
])

export const abstractComponent = z.object({
  type: z.string(),
  displayName: z.string().nullish().optional(),
  componentId: componentIdSchema,
  active: z.boolean().optional(),
  recordingEnabled: z.boolean().optional(),
})

export type ComponentType = z.infer<typeof componentTypeSchema>
